/** Lens Correction v1 — radial barrel/pincushion distortion.
 *
 *  Photoshop's Lens Correction has many tabs (geometric distortion,
 *  chromatic aberration, vignette, perspective transform, etc.). v1
 *  ships the most-used piece: a single signed slider that pulls
 *  pixels toward (pincushion) or away from (barrel) the image
 *  center, correcting (or introducing) the radial distortion a
 *  wide-angle or telephoto lens leaves in an image.
 *
 *  Math: convert each output pixel `(x, y)` to normalized radius
 *  `r ∈ [0, 1]` relative to the image's half-diagonal, then map
 *  `r → r · (1 + k · r²)`. Positive `k` makes near-edge pixels
 *  STRETCH OUTWARD (barrel: corrects pincushion lens images), and
 *  negative `k` pulls pixels INWARD (pincushion: corrects barrel
 *  lens images). The visual sign aligns with Photoshop's "Remove
 *  Distortion" slider: positive removes barrel, but here we expose
 *  it as "add distortion" — positive = barrel look. Easier to
 *  reason about for the creative use case.
 *
 *  Bilinear interpolation on the source sample point avoids the
 *  blocky aliasing nearest-neighbor would produce, especially at
 *  high distortion amounts. */

import { bilinearSample } from './_bilinear';

export interface LensCorrectionValue {
  /** Distortion strength (-100..+100). Positive = barrel (pulls
   *  edges outward); negative = pincushion (pulls edges inward).
   *  0 = identity. */
  amount: number;
}

export const identityLensCorrectionValue = (): LensCorrectionValue => ({ amount: 0 });

export function applyLensCorrectionPixels(
  source: ImageData,
  value: LensCorrectionValue,
): ImageData {
  const w = source.width;
  const h = source.height;
  const amount = Math.max(-100, Math.min(100, value.amount));
  if (amount === 0) {
    return new ImageData(new Uint8ClampedArray(source.data), w, h);
  }
  // Map amount → k coefficient. At amount=100, near-corner pixels
  // (r ≈ 1) move outward by ~50% of the half-diagonal — enough to
  // produce a visibly barrelled but still photographic look. Larger
  // k values rapidly produce unusable warps.
  const k = amount / 200;
  const cx = (w - 1) / 2;
  const cy = (h - 1) / 2;
  // Normalize by the half-diagonal so corner pixels sit at r=1 and
  // the center at r=0 — independent of aspect ratio.
  const halfDiag = Math.sqrt(cx * cx + cy * cy);
  const src = source.data;
  const out = new Uint8ClampedArray(src.length);
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const dx = (x - cx) / halfDiag;
      const dy = (y - cy) / halfDiag;
      const r = Math.sqrt(dx * dx + dy * dy);
      // Output pixel's source sample location: a pixel at radius `r`
      // pulls FROM source radius `r * (1 + k*r²)`. Positive k →
      // source is FURTHER out → output gets pulled outward toward
      // the corners (barrel).
      const rDistorted = r * (1 + k * r * r);
      const scale = r === 0 ? 1 : rDistorted / r;
      const sx = cx + dx * scale * halfDiag;
      const sy = cy + dy * scale * halfDiag;
      bilinearSample(src, w, h, sx, sy, out, (y * w + x) * 4);
    }
  }
  return new ImageData(out, w, h);
}

export function applyLensCorrectionToCanvas(
  source: ImageData,
  target: HTMLCanvasElement,
  value: LensCorrectionValue,
): void {
  const tCtx = target.getContext('2d');
  if (!tCtx) return;
  if (target.width !== source.width || target.height !== source.height) {
    target.width = source.width;
    target.height = source.height;
  }
  tCtx.putImageData(applyLensCorrectionPixels(source, value), 0, 0);
}
